import config from '../../../config';
import {AppRoute} from '../../../models/AppRoute';
import {brandRewardsCopy} from '../../../utils/rewards';
import {LazyRewardsPromoBanner} from '../promoArt';

import {BEER, CIDER, FORTIFIED, PREMIX, RED, SPARKLING, SPIRITS, WHITE} from './departmentJoins';
import {MegaMenu, MenuGroup, MenuLink, MenuTarget, PromoTile} from './menuTypes';

/**
 * The 7 top-level menus in Figma nav-row order (470:18764). DIS-1940 hides the New menu
 * (`id: 'new'`) for the first release. Content from the Figma taxonomy
 * 346:32321 (ported from the prototype ea875fae6 `menuData.ts`).
 *
 * SHORTCUT: static data. Ceiling: one hard-coded tree, Bottlemart content for all 4 brands
 * except the rewards programme name. Upgrade path: `GET /navigation/menus` in lmg-digital-stores
 * returning the same `MegaMenu[]`.
 *
 * Labels are the Figma labels. `path` holds MFL names (spec § 7.3.1), never ids or slugs, so
 * the 9 alias cases of spec § 3.4 keep their Figma label and take the MFL name in `path`.
 * "Non-Alcoholic" takes the hyphen the nav row draws (the taxonomy writes "Non Alcoholic").
 */

/** "More Rewards" is Bottlemart's programme name; every brand shows its own. */
export const brandLabel = (text: string) =>
  text.replace('More Rewards', brandRewardsCopy.loyaltyProgramName);

const NONE: MenuTarget = {kind: 'none'};

const live = (label: string, target: MenuTarget): MenuLink => ({label, target, status: 'live'});
const disabled = (label: string, target: MenuTarget, reason: string): MenuLink => ({
  label,
  target,
  status: 'disabled',
  reason,
});
const hidden = (label: string, target: MenuTarget, reason: string): MenuLink => ({
  label,
  target,
  status: 'hidden',
  reason,
});

// Targets.
const department = (...path: string[]): MenuTarget => ({kind: 'department', path});
/** DIS-1900 D1: `path`, then each `altPaths` entry in order; the first that resolves wins. */
const departmentOr = (path: string[], ...altPaths: string[][]): MenuTarget => ({
  kind: 'department',
  path,
  altPaths,
});
const SPECIALS: MenuTarget = {kind: 'specials'};
const wineSpecials: MenuTarget = {kind: 'departments', join: 'wine', specials: true};
const beerCiderSpecials: MenuTarget = {kind: 'departments', join: 'beer-cider', specials: true};
const spiritsSpecials: MenuTarget = {kind: 'departments', join: 'spirits', specials: true};
const premixSpecials: MenuTarget = {kind: 'departments', join: 'premix', specials: true};

// MFL names (Terminus Hotel, spec § 3.4). Not verified against any other store. The join names
// live in departmentJoins.ts.
const SOFT = 'Soft Drinks, Snacks & Extras';
// DIS-1900 D1: BM, SNS and HB test shops split SOFT into these two departments (TC keeps SOFT).
const SOFT_SNACKS = 'Soft Drinks & Snacks';
const EXTRAS = 'Extras';
// DIS-1900 R2 placeholder: no store has this department yet. A link that does not resolve is
// dropped and reported as MegaMenuDrift, and appears once merchandising creates the department.
const GIFTS = 'Gifts';

// DIS-1900 R1: no link ships `disabled`. The only `kind: 'none'` links are plain-label headings.
const NOT_A_LINK = 'Group label only: no MFL department or route behind it';

/** A group label that does not link (`headingIsLink: false`). `kind: 'none'` is never live. */
const label = (text: string): MenuLink => disabled(text, NONE, NOT_A_LINK);

/**
 * A Popular Brands link. The label is the Figma label; `target.brand` is the verbatim Airtable
 * `brand` (R51), equal to the label unless `airtableName` is given. DIS-1900 R3: every brand
 * link is live; one with no Airtable match is omitted once brands load.
 */
const brand = (name: string, airtableName = name): MenuLink =>
  live(name, {kind: 'brand', brand: airtableName});

/** `headingIsLink` is transcribed from taxonomy-figma.json, never derived from the target (R53). */
const group = (
  heading: MenuLink,
  column: number,
  items: MenuLink[] = [],
  rest: Partial<MenuGroup> = {},
): MenuGroup => ({heading, headingIsLink: true, column, items, ...rest});

/** A group whose heading is a plain label (`headingIsLink: false`, R52). `rest` keeps Shop All. */
const plainGroup = (
  text: string,
  column: number,
  items: MenuLink[],
  rest: Partial<MenuGroup> = {},
): MenuGroup => group(label(text), column, items, {...rest, headingIsLink: false});

/** DIS-1900: a Shop All department link opens the department's view-all product list. */
const viewAll = (target: MenuTarget): MenuTarget =>
  target.kind === 'department' ? {...target, viewAll: true} : target;

/** A group's "Shop All" shares the group heading's target (dropdown frames 196:20379–196:20385). */
const shopAllOf = (target: MenuTarget): MenuLink => live('Shop All', viewAll(target));

// SHORTCUT: the rewards banner is per-brand Figma artwork bundled in the app
// (RewardsBanner.tsx, promo-imagedesigns 115:8839 / 468:6345 / 468:6355 / 468:6365), with a
// hover state. Upgrade path: per-brand banner image from the menus API. All promo artwork loads
// lazily (R63), so it stays out of the main web chunk.
const REWARDS_BANNER: PromoTile = {
  label: `Explore member ${brandRewardsCopy.loyaltyProgramName}`,
  wide: true,
  image: LazyRewardsPromoBanner,
  action: {kind: 'route', route: AppRoute.REWARDS},
};
const PROMOS_BANNER: PromoTile[] = [REWARDS_BANNER];

// DIS-1900 D2: the More Rewards Specials link opens the member-specials product set.
// DIS-1916: only the Specials menu carries it. The member-specials route has no department
// filter, so per-category copies all opened the same unfiltered list.
const MEMBER_SPECIALS: MenuTarget = {
  kind: 'route',
  route: AppRoute.MEMBERS_PRICE_LIST,
  params: {external_sell_type: 'member-specials'},
};
const memberSpecials = (text: string) => live(brandLabel(text), MEMBER_SPECIALS);
/** A DIS-1900 R2 placeholder under the Gifts department; the child name equals the label. */
const gift = (text: string) => live(text, department(GIFTS, text));

const redWine = department(RED);
const whiteWine = department(WHITE);
const sparkling = department(SPARKLING);
const beer = department(BEER);
// The Cider group maps to the "Cider" sub-department because the Ginger Beer sub-department has its
// own link in the Beer group. The department name "Cider & Ginger Beer" has no Figma label.
const cider = department(CIDER, 'Cider');

export const megaMenus: MegaMenu[] = [
  {
    id: 'specials',
    flag: 'MEGA_MENU_SPECIALS',
    label: 'Specials',
    mainLinks: [live('Shop All Specials', SPECIALS), memberSpecials('More Rewards Specials')],
    groups: [
      // DIS-1939 supersedes the R53 exception. Figma draws "Current Specials" as a plain
      // label and the heading does not navigate. Catalogue stays a link.
      plainGroup('Current Specials', 0, [
        live('Catalogue', {kind: 'route', route: AppRoute.CATALOGUE}),
      ]),
      plainGroup('By Type', 1, [
        live('Beer & Cider Specials', beerCiderSpecials),
        live('Wine Specials', wineSpecials),
        live('Spirit Specials', spiritsSpecials),
        live('Premix Specials', premixSpecials),
      ]),
    ],
    promos: PROMOS_BANNER,
  },
  {
    id: 'beer-cider',
    flag: 'MEGA_MENU_BEER_CIDER',
    label: 'Beer & Cider',
    mainLinks: [
      live('Shop All Beer & Cider', {kind: 'departments', join: 'beer-cider'}),
      live('Shop Beer & Cider Specials', beerCiderSpecials),
    ],
    groups: [
      plainGroup(
        'Beer',
        0,
        [
          live('Easy Drinking & Low Carb Beer', department(BEER, 'Easy Drinking & Low Carb Beer')),
          live('Full Strength Beer', department(BEER, 'Full Strength Beer')),
          live('Mid Strength Beer', department(BEER, 'Mid Strength Beer')),
          live('International Beer', department(BEER, 'International Beer')),
          live('Australian Premium Beer', department(BEER, 'Australian Premium Beer')),
          live('Craft Beer', department(BEER, 'Craft Beer')),
          live('Light & Non-Alcoholic Beer', department(BEER, 'Light & Non Alcoholic Beer')),
          live('Ginger Beer', department(CIDER, 'Ginger Beer')),
        ],
        {shopAll: shopAllOf(beer)},
      ),
      plainGroup('Cider', 1, [], {shopAll: shopAllOf(cider)}),
      plainGroup('Popular Brands', 1, [
        brand('XXXX'),
        brand('Toohey’s'),
        brand('Great Northern Brewing', 'Great Northern Brewing Co'),
        brand('Carlton Dry'),
        brand('Victoria Bitter'),
        brand('Coopers'),
        brand('Hahn'),
      ]),
    ],
    promos: PROMOS_BANNER,
  },
  {
    id: 'wine',
    flag: 'MEGA_MENU_WINE',
    label: 'Wine',
    mainLinks: [
      live('Shop All Wine', {kind: 'departments', join: 'wine'}),
      live('Shop Wine Specials', wineSpecials),
    ],
    groups: [
      plainGroup(
        'Red Wine',
        0,
        [
          live('Cabernet Blends', department(RED, 'Cabernet Blends')),
          live('Cabernet Sauvignon', department(RED, 'Cabernet Sauvignon')),
          live('Grenache', department(RED, 'Grenache')),
          live('Merlot', department(RED, 'Merlot')),
          live('Other Red Wines', department(RED, 'Other Red Wines')),
          live('Pinot Noir', department(RED, 'Pinot Noir')),
          live('Red Cask Wine', department(RED, 'Red Cask Wine')),
          live('Rose', department(RED, 'Rosé')),
          live('Shiraz', department(RED, 'Shiraz')),
          live('Shiraz Blends', department(RED, 'Shiraz Blends')),
          live('Sweet Reds', department(RED, 'Sweet Reds')),
        ],
        {shopAll: shopAllOf(redWine)},
      ),
      plainGroup(
        'White Wine',
        1,
        [
          live('Chardonnay', department(WHITE, 'Chardonnay')),
          live('Dessert Wine', department(WHITE, 'Dessert Wine')),
          live('Moscato & Sweet Wine', department(WHITE, 'Moscato & Sweet White')),
          live('Other White Wine', department(WHITE, 'Other')),
          live('Pinot Gris / Grigio', department(WHITE, 'Pinot Gris / Grigio')),
          live('Riesling', department(WHITE, 'Riesling')),
          live('Sauvignon Blanc', department(WHITE, 'Sauvignon Blanc')),
          live('Semillon Sauvignon Blanc', department(WHITE, 'Semillon Sauvignon Blanc')),
          live('White Cask Wine', department(WHITE, 'White Cask Wine')),
        ],
        {shopAll: shopAllOf(whiteWine)},
      ),
      plainGroup(
        'Champagne & Sparkling',
        2,
        [
          live('Champagne', department(SPARKLING, 'Champagne')),
          live('Prosecco', department(SPARKLING, 'Prosecco')),
          live('Sparkling Red Wine', department(SPARKLING, 'Sparkling Red')),
          live('Sparkling Rose', department(SPARKLING, 'Sparkling Rosé')),
          live('Sparkling Sweet', department(SPARKLING, 'Sparkling Sweet')),
          live('Sparkling White Wine', department(SPARKLING, 'Sparkling White')),
          // Measured 2026-11-04: the Terminus fixture has this department; the Springdale and Kings
          // trees do not. A store without it drops this link and reports MegaMenuDrift.
          live('Spritz', department(SPARKLING, 'Spritz')),
        ],
        {shopAll: shopAllOf(sparkling)},
      ),
      plainGroup(
        'Other Wine',
        3,
        [
          live('Fortified Cask Wine', department(FORTIFIED, 'Fortified Cask Wine')),
          live('Fortified Wine', department(FORTIFIED, 'Fortified Wine')),
        ],
        {shopAll: shopAllOf(department(FORTIFIED))},
      ),
      plainGroup('Popular Brands', 3, [
        brand('Penfold’s', 'Penfolds'),
        brand('Jacob’s Creek', 'Jacobs Creek'),
        brand('Veuve Clicquot'),
        brand('Moet & Chandon', 'Moet'),
      ]),
    ],
    promos: PROMOS_BANNER,
  },
  {
    id: 'spirits',
    flag: 'MEGA_MENU_SPIRITS',
    label: 'Spirits',
    mainLinks: [
      live('Shop All Spirits', viewAll(department(SPIRITS))),
      live('Shop Spirits Specials', spiritsSpecials),
    ],
    groups: [
      plainGroup(
        'Spirits',
        0,
        [
          live('Scotch & Whisky', department(SPIRITS, 'Scotch & Whisky')),
          live('Bourbon', department(SPIRITS, 'Bourbon')),
          live('Vodka', department(SPIRITS, 'Vodka')),
          live('Rum', department(SPIRITS, 'Rum')),
          live('Gin', department(SPIRITS, 'Gin')),
          live('Tequila', department(SPIRITS, 'Tequila')),
          live('Brandy & Cognac', department(SPIRITS, 'Brandy & Cognac')),
          live('Ouzo', department(SPIRITS, 'Ouzo')),
          // DIS-1916 D4 deviation: not in the ticket list; kept because it resolves at 4 of 4 test shops.
          live('Liqueurs & Other', department(SPIRITS, 'Liqueurs & Other')),
        ],
        {shopAll: shopAllOf(department(SPIRITS))},
      ),
      plainGroup('Popular Brands', 1, [
        brand('Jack Daniel’s', "Jack Daniel's"),
        brand('Johnnie Walker'),
        brand('Jim Beam'),
        brand('Bundaberg'),
        brand('Jameson'),
        brand('Absolut Vodka'),
        brand('Wild Turkey'),
        brand('Baileys'),
        brand('Chivas Regal'),
      ]),
    ],
    promos: PROMOS_BANNER,
  },
  {
    id: 'premix',
    flag: 'MEGA_MENU_PREMIX',
    label: 'Premix',
    mainLinks: [
      live('Shop All Premix', viewAll(department(PREMIX))),
      live('Shop Premix Specials', premixSpecials),
    ],
    groups: [
      plainGroup(
        'Premix',
        0,
        [
          live('Bourbon', department(PREMIX, 'Bourbon')),
          live('Vodka', department(PREMIX, 'Vodka')),
          live('Rum', department(PREMIX, 'Rum')),
          live('Scotch & Whisky', department(PREMIX, 'Scotch & Whisky')),
          live('Seltzer', department(PREMIX, 'Seltzer')),
          live('Gin', department(PREMIX, 'Gin')),
          live('Liqueur & Other', department(PREMIX, 'Liqueur & Other')),
        ],
        {shopAll: shopAllOf(department(PREMIX))},
      ),
      // DIS-1916: the ticket list, in ticket order. 'Hard Rated' appears once (Q10).
      plainGroup('Popular Brands', 1, [
        brand('Hard Rated'),
        brand('Jack Daniel’s', "Jack Daniel's"),
        brand('Jim Beam'),
        brand('Bundaberg Rum', 'Bundaberg'),
        brand('Wild Turkey'),
        brand('Vodka Cruiser'),
        brand('Canadian Club'),
      ]),
    ],
    promos: PROMOS_BANNER,
  },
  {
    id: 'non-alcoholic',
    flag: 'MEGA_MENU_NON_ALCOHOLIC',
    label: 'Non-Alcoholic',
    mainLinks: [],
    groups: [
      // DIS-2054: the R3 Figma groups and order, with no Shop All. Each group spans departments, so
      // its heading is a plain label. Cocktail Mixers & Cordials is the DIS-1916 display label.
      plainGroup('Drinks & Mixers', 0, [
        live(
          'Non-Alcoholic',
          departmentOr([SOFT, 'Non-Alcoholic'], [SOFT_SNACKS, 'Non-Alcoholic']),
        ),
        live(
          'Soft Drinks & Energy Drinks',
          departmentOr(
            [SOFT, 'Soft Drinks & Energy Drinks'],
            [SOFT_SNACKS, 'Soft Drinks & Energy Drinks'],
          ),
        ),
        live(
          'Water & Juice',
          departmentOr([SOFT, 'Water & Juice'], [SOFT_SNACKS, 'Water & Juice']),
        ),
        live(
          'Cocktail Mixers & Cordials',
          departmentOr([SOFT, 'Cordials & Syrups'], [SOFT_SNACKS, 'Cordials & Syrups']),
        ),
      ]),
      plainGroup('Food & Snacks', 1, [
        live('Snacks', departmentOr([SOFT, 'Snacks'], [SOFT_SNACKS, 'Snacks'])),
        live('Ice', departmentOr([SOFT, 'Ice'], [EXTRAS, 'Ice'])),
        // Measured 2026-11-04: store C000192522 returned 14 lines with a session and 0 without one.
        {
          ...live(
            'Cigarettes & Tobacco',
            departmentOr([SOFT, 'Cigarettes & Tobacco'], [EXTRAS, 'Cigarettes & Tobacco']),
          ),
          signedInOnly: true,
        },
        live('Accessories', departmentOr([SOFT, 'Accessories'], [EXTRAS, 'Accessories'])),
      ]),
    ],
    promos: PROMOS_BANNER,
  },
  {
    id: 'gifts',
    flag: 'MEGA_MENU_GIFTS',
    label: 'Gifts & Occasions',
    mainLinks: [
      live(
        'Shop All Gifts',
        viewAll(departmentOr([GIFTS], [EXTRAS, 'Accessories', 'Gifts'], [SOFT, 'Gifting'])),
      ),
    ],
    groups: [
      group(
        config.FF_IS_GIFT_CARDS_ENABLED
          ? live('Gift Cards', {kind: 'route', route: AppRoute.ROOT_GIFT_CARDS_PROMOTION})
          : hidden(
              'Gift Cards',
              {kind: 'route', route: AppRoute.ROOT_GIFT_CARDS_PROMOTION},
              'FF_IS_GIFT_CARDS_ENABLED is off for this brand',
            ),
        0,
      ),
      plainGroup('Type', 1, [
        gift('Spirit Gifts'),
        gift('Wine Gifts'),
        gift('Champagne & Sparkling Gifts'),
      ]),
      plainGroup('Price Range', 2, [
        gift('Under $30'),
        gift('Under $50'),
        gift('Under $100'),
        gift('Above $100'),
      ]),
      plainGroup('Occasions', 3, [
        gift('At Home Hosting'),
        gift('Summer Entertaining'),
        gift('Weddings & Celebrations'),
      ]),
    ],
    promos: PROMOS_BANNER,
  },
];
